<template>
  <div class="wbs-tree">
    <div v-for="node in nodes" :key="node.id" class="wbs-node">
      <!-- ================= GRUPO ================= -->
      <template v-if="node.type === 'group'">
        <div
          class="wbs-row wbs-row--group"
          :style="{ background: tint(node.color, 0.06), boxShadow: 'inset 4px 0 0 0 ' + (node.color || ACCENT) }"
          @click="toggle(node)"
        >
          <span class="wbs-chev" :class="{ 'wbs-chev--open': isOpen(node) }">
            <b-icon v-if="node._loading" icon="spinner" pack="fas" size="is-small" class="fa-spin"></b-icon>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>
          </span>
          <span class="wbs-code-badge" :style="{ background: node.color || ACCENT }">{{ node.code }}</span>
          <span class="wbs-group-title">{{ node.title }}</span>
          <span v-if="node.count != null" class="wbs-count">{{ node.count }}</span>
        </div>
      </template>

      <!-- ================= ACTIVIDAD ================= -->
      <template v-else-if="node.type === 'activity'">
        <div class="wbs-row wbs-row--activity" :style="{ paddingLeft: pad }" @click="toggle(node)">
          <span class="wbs-chev" :class="{ 'wbs-chev--open': isOpen(node) }">
            <b-icon v-if="node._loading" icon="spinner" pack="fas" size="is-small" class="fa-spin"></b-icon>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>
          </span>
          <span class="wbs-mono">{{ node.code }}</span>
          <span class="wbs-act-title">{{ node.title }}</span>
          <span v-if="node.clientTag" class="wbs-tag wbs-tag--client-strong" :title="$t('client_program')">{{ node.clientTag }}</span>
          <span v-if="node.count != null" class="wbs-act-count">{{ node.count }}</span>
        </div>
      </template>

      <!-- ================= SUBACTIVIDAD ================= -->
      <template v-else-if="node.type === 'subactivity'">
        <div class="wbs-row wbs-row--sub" :style="{ paddingLeft: pad }" @click="toggle(node)">
          <span class="wbs-chev wbs-chev--sub" :class="{ 'wbs-chev--open': isOpen(node) }">
            <b-icon v-if="node._loading" icon="spinner" pack="fas" size="is-small" class="fa-spin"></b-icon>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>
          </span>
          <span class="min-w-0">
            <span class="wbs-sub-title">{{ node.title }}</span>
            <span v-if="node.meta" class="wbs-sub-meta">{{ node.meta }}</span>
          </span>
          <span v-if="node.ecPill" class="wbs-ec-pill">{{ node.ecPill }}</span>
        </div>
      </template>

      <!-- ================= MEDICIÓN / EC (hoja) ================= -->
      <template v-else>
        <div
          class="wbs-row wbs-row--ec"
          :class="{
            'wbs-row--ec-checked': isChecked(node),
            'wbs-row--ec-match': unitMatches(node) && !lockedByOther(node),
            'wbs-row--ec-nomatch': !unitMatches(node) && !lockedByOther(node),
            'wbs-row--ec-locked': lockedByOther(node),
          }"
          :style="ecStyle(node)"
          :title="lockedByOther(node) ? $t('linked_to_another_boq') : (!unitMatches(node) ? $t('unit_mismatch') : null)"
        >
          <input
            type="checkbox"
            class="wbs-check"
            :checked="isChecked(node) || lockedByOther(node)"
            :disabled="lockedByOther(node) || !unitMatches(node) || (coverageFull && !isChecked(node))"
            @change="toggleCheck(node)"
          />
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="wbs-ec-icon"><path d="M13 5h8"></path><path d="M13 12h8"></path><path d="M13 19h8"></path><path d="m3 17 2 2 4-4"></path><path d="m3 7 2 2 4-4"></path></svg>
          <span class="wbs-ec-title" :style="{ fontWeight: isChecked(node) ? 600 : 400 }">{{ node.title }}</span>
          <span v-if="node.unit" class="wbs-tag wbs-tag--unit">{{ node.unit }}</span>
          <span v-if="lockedByOther(node)" class="wbs-tag wbs-tag--locked">{{ $t('linked_to_another_boq') }}</span>
          <span v-if="node.clientTag" class="wbs-tag wbs-tag--client">{{ $t('client') }}: {{ node.clientTag }}</span>
          <span v-if="node.boqTag" class="wbs-tag wbs-tag--boq">BOQ: {{ node.boqTag }}</span>
          <span v-if="isChecked(node)" class="wbs-qty">
            <!-- Editable solo si el padre lo habilita; por defecto la cantidad es la
                 propia de la medición (no se reparte) y se muestra como texto. -->
            <input
              v-if="editableQuantity"
              inputmode="decimal"
              type="text"
              class="wbs-qty__input"
              :value="qtyOf(node)"
              @input="setQty(node, $event.target.value)"
            />
            <span v-else class="wbs-qty__text">{{ qtyOf(node) }}</span>
            <span class="wbs-qty__unit">{{ node.unit || unit }}</span>
          </span>
        </div>
      </template>

      <!-- HIJOS (recursivo, cargados de forma lazy) -->
      <template v-if="isOpen(node)">
        <div v-if="node._loaded && (!node.children || !node.children.length)" class="wbs-empty" :style="{ paddingLeft: pad }">
          {{ $t('no_results') }}
        </div>
        <wbs-tree
          v-else
          :nodes="node.children"
          :level="level + 1"
          :unit="unit"
          :loader="loader"
          :checked="checked"
          :quantities="quantities"
          :editable-quantity="editableQuantity"
          :current-boq-id="currentBoqId"
          :coverage-full="coverageFull"
          @check="$emit('check', $event)"
          @qty="$emit('qty', $event)"
        />
      </template>
    </div>
  </div>
</template>

<script>
/**
 * WbsTree — árbol WBS jerárquico con carga lazy: grupo → (subgrupos | actividad) →
 * subactividad → medición/EC (hoja con checkbox + cantidad). Recursivo, dirigido por
 * `type` de cada nodo.
 *
 * Carga lazy: al expandir un nodo con `_expandable` y sin `_loaded`, se invoca la
 * función `loader(node)` (la provee el padre) que devuelve una Promise con los hijos;
 * el resultado se guarda en `node.children` y se marca `node._loaded`. El estado de
 * expansión/carga vive en el propio nodo (`_open`, `_loaded`, `_loading`), que es un
 * objeto compartido por referencia en toda la recursión.
 *
 * Selección: `checked` (mapa {ecId:true}) y `quantities` (mapa {ecId:valor}) son props
 * controladas por el padre (fuente única de verdad). Los cambios se emiten con
 * `check`/`qty` y burbujean por la recursión.
 *
 * Nodo: { id, type:'group'|'activity'|'subactivity'|'ec', code, title, color?, count?,
 *         meta?, clientTag?, boqTag?, ecPill?, unit?, ecId?,
 *         _expandable?, _open?, _loaded?, _loading?, children? }
 */
export default {
  name: 'wbs-tree',

  props: {
    nodes: { type: Array, default: () => [] },
    level: { type: Number, default: 0 },
    unit: { type: String, default: '' },
    // Función async (node) => Promise<childNodes[]> para la carga lazy de hijos.
    loader: { type: Function, default: null },
    // Selección controlada desde el padre (por id de medición/EC).
    checked: { type: Object, default: () => ({}) },
    quantities: { type: Object, default: () => ({}) },
    // Si false (por defecto), la cantidad del EC se muestra como texto (no editable);
    // si true, se muestra un input editable que emite `qty`.
    editableQuantity: { type: Boolean, default: false },
    // Id de la línea BOQ actual: para bloquear mediciones ya vinculadas a OTRA línea.
    currentBoqId: { type: [Number, String], default: null },
    // Cobertura completa (pendiente <= 0): deshabilita marcar MÁS mediciones (las ya
    // marcadas siguen desmarcables). Permite una sola que cruce el total (excedente para Split).
    coverageFull: { type: Boolean, default: false },
  },

  data() {
    return {
      ACCENT: 'rgb(58, 107, 200)',
    }
  },

  computed: {
    // Sangría por nivel para actividades/subactividades/EC.
    pad() {
      return (16 + this.level * 22) + 'px'
    },
  },

  methods: {
    hasChildren(n) {
      return !!n._expandable
    },
    isOpen(n) {
      return !!n._open
    },
    async toggle(n) {
      if (!n._expandable) return

      // Ya cargado (o sin loader): simple toggle.
      if (n._loaded || !this.loader) {
        this.$set(n, '_open', !n._open)
        return
      }

      // Primera expansión: cargar hijos vía loader.
      this.$set(n, '_loading', true)
      try {
        const children = await this.loader(n)
        this.$set(n, 'children', Array.isArray(children) ? children : [])
        this.$set(n, '_loaded', true)
        this.$set(n, '_open', true)
      } catch (e) {
        this.$set(n, 'children', [])
        this.$set(n, '_loaded', true)
        this.$set(n, '_open', true)
      } finally {
        this.$set(n, '_loading', false)
      }
    },
    isChecked(n) {
      return !!this.checked[n.ecId != null ? n.ecId : n.id]
    },
    // Normaliza una unidad para comparar (sin espacios, minúsculas).
    _normUnit(u) {
      return String(u == null ? '' : u).trim().toLowerCase()
    },
    // Una medición solo se puede marcar si su unidad coincide con la del BOQ (`unit`).
    // Si no hay unidad de BOQ definida, no se restringe (todas marcables).
    unitMatches(n) {
      const boqUnit = this._normUnit(this.unit)
      if (!boqUnit) return true
      return this._normUnit(n.unit) === boqUnit
    },
    // La medición ya está vinculada a OTRA línea BOQ (no a la actual) → no se puede tocar.
    lockedByOther(n) {
      if (n.linkedBoqId == null || this.currentBoqId == null) return false
      return Number(n.linkedBoqId) !== Number(this.currentBoqId)
    },
    toggleCheck(n) {
      if (this.lockedByOther(n)) return // ya vinculada a otra BOQ
      if (!this.unitMatches(n)) return  // unidad no coincide: no marcable
      this.$emit('check', { node: n, checked: !this.isChecked(n) })
    },
    qtyOf(n) {
      const key = n.ecId != null ? n.ecId : n.id
      return this.quantities[key] != null ? this.quantities[key] : '0'
    },
    setQty(n, v) {
      this.$emit('qty', { node: n, value: v })
    },
    ecStyle(n) {
      const base = { paddingLeft: (this.pad) }
      if (this.isChecked(n)) {
        base.background = 'rgba(58,107,200,.05)'
        base.boxShadow = 'inset 3px 0 0 0 rgb(58,107,200)'
      }
      return base
    },
    // Tinte rgba a partir de un color rgb(...) del nodo.
    tint(color, alpha) {
      const c = color || this.ACCENT
      const m = c.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      if (!m) return c
      return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`
    },
  },
}
</script>

<style scoped>
.wbs-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #f3f4f6;
  cursor: pointer;
}
.wbs-chev {
  flex-shrink: 0;
  color: #9ca3af;
  display: inline-flex;
  transition: transform 120ms ease;
}
.wbs-chev--open { transform: rotate(90deg); }

/* Grupo */
.wbs-row--group:hover { filter: brightness(0.98); }
.wbs-code-badge {
  flex-shrink: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9.5px;
  font-weight: 700;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px;
}
.wbs-group-title {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .02em;
  color: rgb(30, 48, 70);
}
.wbs-count { margin-left: auto; font-size: 10px; color: #9ca3af; }

/* Actividad */
.wbs-row--activity:hover { background: #f4f7fc; }
.wbs-mono { flex-shrink: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 9.5px; color: #9ca3af; }
.wbs-act-title { font-size: 11px; font-weight: 500; color: rgb(30, 48, 70); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wbs-act-count { margin-left: auto; flex-shrink: 0; font-size: 9.5px; color: #9ca3af; }

/* Subactividad */
.wbs-row--sub { background: #fafbfc; }
.wbs-chev--sub { color: #9ca3af; }
.wbs-sub-title { display: block; font-size: 10.5px; font-weight: 600; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wbs-sub-meta { display: block; font-size: 9px; color: #9ca3af; margin-top: 2px; }
.wbs-ec-pill { margin-left: auto; flex-shrink: 0; font-size: 9px; font-weight: 600; border-radius: 9999px; padding: 2px 8px; color: rgb(58,107,200); background: rgba(58,107,200,.07); }

/* Medición / EC */
.wbs-row--ec:hover { background: #f4f8ff; }
/* Unidad coincide con la del BOQ → fila marcable, color distintivo (verde). */
.wbs-row--ec-match {
  background: rgba(39,174,96,.06);
  box-shadow: inset 3px 0 0 0 rgb(39,174,96);
}
.wbs-row--ec-match:hover { background: rgba(39,174,96,.12); }
/* Unidad NO coincide → no marcable (atenuada). */
.wbs-row--ec-nomatch {
  opacity: .5;
  cursor: not-allowed;
}
.wbs-row--ec-nomatch:hover { background: transparent; }
/* Ya vinculada a OTRA línea BOQ → visible, checked pero no tocable. */
.wbs-row--ec-locked {
  background: #f6f7f9;
  cursor: not-allowed;
}
.wbs-row--ec-locked:hover { background: #f6f7f9; }
.wbs-tag--locked { color: #92400e; background: #fef3c7; }
.wbs-check { cursor: pointer; accent-color: rgb(58,107,200); flex-shrink: 0; }
.wbs-check:disabled { cursor: not-allowed; }
.wbs-ec-icon { color: rgb(58,107,200); flex-shrink: 0; }
.wbs-ec-title { font-size: 10.5px; color: rgb(30, 48, 70); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wbs-tag {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 8.5px;
}
.wbs-tag--client { color: rgb(91,63,145); background: rgb(240,235,250); }
.wbs-tag--client-strong { margin-left: auto; font-weight: 600; color: rgb(91,63,145); background: rgb(240,235,250); border: 1px solid rgb(216,202,239); }
.wbs-tag--boq { color: rgb(28,130,70); background: rgba(39,174,96,.08); }
.wbs-tag--unit { color: #55627d; background: #eef1f6; font-weight: 600; }
.wbs-qty { margin-left: auto; display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0; }
.wbs-qty__input { width: 6rem; text-align: right; font-size: 10.5px; border: 1px solid #d1d5db; border-radius: 4px; padding: 2px 6px; font-variant-numeric: tabular-nums; }
.wbs-qty__input:disabled { background: #f3f4f6; color: #6b7280; cursor: not-allowed; }
.wbs-qty__unit { font-size: 9.5px; color: #9ca3af; }
.wbs-qty__text { font-size: 10.5px; font-weight: 600; color: rgb(30,48,70); font-variant-numeric: tabular-nums; }
.wbs-empty { padding-top: 6px; padding-bottom: 6px; font-size: 10px; color: #9ca3af; font-style: italic; }
.min-w-0 { min-width: 0; }
</style>
